/**
 * CanvasCore — the ONLY place Fabric.js is mutated.
 *
 * Exposes an imperative handle (forwardRef + useImperativeHandle) the parent
 * orchestrator calls for every action (export, undo/redo, z-order, lock, group,
 * zoom, style edits, image add, load/save). Geometry lives in Fabric; the
 * Zustand store only mirrors intent + summaries.
 *
 * Input: works with mouse, trackpad, touch and stylus / Apple Pencil.
 *   • pen / stylus                    → always draws (native Fabric)
 *   • single finger                   → draw / select / drag, unless finger-drawing
 *                                       is off (then it pans on creation tools)
 *   • two fingers                     → pinch-zoom + pan (custom, capture-phase)
 *   • wheel                           → pan; ⌘/Ctrl+wheel or trackpad pinch → zoom
 *   • Space-drag or Hand tool         → pan
 *
 * Palm rejection (pen-priority): the capture-phase pointer listeners on wrapperEl
 * are the single gatekeeper for touch. While a pen is down, every touch is
 * swallowed (preventDefault + stopPropagation) so Fabric never sees the palm —
 * the pen stroke is never interrupted and a resting palm can't start a pinch.
 * The first stylus contact also auto-disables finger drawing (store.fingerDrawing).
 */
import React, {
  forwardRef, useImperativeHandle, useEffect, useRef, useCallback,
} from 'react';
import * as fabric from 'fabric';
// EraserBrush gives Photoshop-style partial erasing (commits eraser paths into
// a ClippingGroup clipPath). Importing also registers ClippingGroup in Fabric's
// classRegistry so erased objects survive JSON save/load and undo/redo.
import { EraserBrush } from '@erase2d/fabric';
import { useCanvasStore } from './canvasStore';
import {
  SHAPE_DEFAULTS, TEXT_DEFAULTS, STICKY_COLORS, BACKGROUNDS, EXTRA_PROPS,
  ZOOM_MIN, ZOOM_MAX, MAX_HISTORY, MAX_OBJECTS, MAX_IMAGE_SIZE, uid,
} from './canvasConstants';
import { toast } from '../../../utils/toast';

const clamp = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

// 5-point star points centred on (0,0).
const starPoints = (outer = 50, inner = 22, spikes = 5) => {
  const pts = [];
  const step = Math.PI / spikes;
  let rot = -Math.PI / 2;
  for (let i = 0; i < spikes; i++) {
    pts.push({ x: Math.cos(rot) * outer, y: Math.sin(rot) * outer }); rot += step;
    pts.push({ x: Math.cos(rot) * inner, y: Math.sin(rot) * inner }); rot += step;
  }
  return pts;
};
const polygonPoints = (sides = 6, r = 50) =>
  Array.from({ length: sides }, (_, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / sides;
    return { x: Math.cos(a) * r, y: Math.sin(a) * r };
  });
// Diamond (decision) points for a w×h bounding box, origin top-left.
const diamondPoints = (w, h) => [
  { x: w / 2, y: 0 }, { x: w, y: h / 2 }, { x: w / 2, y: h }, { x: 0, y: h / 2 },
];

// ── Flowchart "nodes" (shape + wrapping label in one group) ────────────────────
const isNode = (o) => o?.type === 'group' && /^flowchart_/.test(o.customType || '');
const nodeText = (g) => g.getObjects?.().find((o) => o.type === 'textbox');
const NODE_PAD_X = 12;
const NODE_PAD_Y = 10;
// Inner text width for a node: rects keep side padding; the diamond's inscribed
// area is narrower, so its label gets ~55% of the width.
const nodeTextWidth = (tool, w) =>
  tool === 'flowchart_decision' ? Math.max(24, w * 0.55) : Math.max(24, w - NODE_PAD_X * 2);
const NODE_STYLES = {
  flowchart_start: { fill: '#ecfdf5', stroke: '#059669', label: 'Start' },
  flowchart_process: { fill: '#eff6ff', stroke: '#2563eb', label: 'Process' },
  flowchart_decision: { fill: '#fef3c7', stroke: '#d97706', label: 'Decision?' },
};

// Tools that create ink or shapes — these are the ones a finger is blocked from
// when finger-drawing is off (select / hand stay usable by touch for navigation).
const DRAW_TOOLS = new Set([
  'pen', 'brush', 'eraser', 'rectangle', 'circle', 'triangle', 'line', 'arrow',
  'star', 'polygon', 'text', 'sticky',
  'flowchart_start', 'flowchart_process', 'flowchart_decision',
]);

const CanvasCore = forwardRef(function CanvasCore({ onReady, onChange }, ref) {
  const wrapRef = useRef(null);       // sizing container (.cv-stage)
  const canvasElRef = useRef(null);   // <canvas>
  const fcRef = useRef(null);         // fabric.Canvas
  const paperRef = useRef(null);      // .canvas-container (Fabric wrapperEl) — paper background
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;     // latest change callback (for live collab broadcast)

  // Live values for stable event handlers (avoid rebinding on every change).
  const live = useRef({
    tool: 'select', brushColor: '#0f172a', brushWidth: 3,
    snapEnabled: true, gridSize: 20, stickyColorIndex: 0, background: 'dots',
    spaceHeld: false, fingerDrawing: true,
  });

  const history = useRef({ stack: [], index: -1, applying: false });
  const clipboard = useRef(null);
  const draft = useRef(null);         // shape being drag-sized
  const startPt = useRef(null);
  const panning = useRef(null);       // {x,y} last client pos while panning
  const pointers = useRef(new Map()); // pointerId -> {x,y} (for pinch)
  const gesture = useRef(null);
  const penDown = useRef(false);      // a stylus is currently touching the surface

  const store = useCanvasStore;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const scenePoint = useCallback((e) => {
    const c = fcRef.current;
    return c.getScenePoint ? c.getScenePoint(e) : c.getPointer(e);
  }, []);

  // Centre of the visible viewport in scene coordinates (for placing templates).
  const vpCenter = useCallback(() => {
    const c = fcRef.current;
    if (c.getVpCenter) return c.getVpCenter();
    const vpt = c.viewportTransform; const z = vpt[0];
    return new fabric.Point((c.getWidth() / 2 - vpt[4]) / z, (c.getHeight() / 2 - vpt[5]) / z);
  }, []);

  const withMeta = useCallback((obj, customType) => {
    obj.set({ id: uid(), customType, locked: false, erasable: true });
    return obj;
  }, []);

  // Derive the runtime lock behaviour from the serialised `locked` flag. A
  // locked object stays clickable (so it can be selected and unlocked) but
  // can't be moved, scaled, rotated, edited or erased. lockMovementX etc. are
  // NOT serialised — only `locked` survives save/load/undo — so this must be
  // re-applied after every loadFromJSON (applyToolConfig covers those paths).
  const applyLockState = useCallback((o) => {
    const lock = !!o.locked;
    o.set({
      lockMovementX: lock, lockMovementY: lock, lockRotation: lock,
      lockScalingX: lock, lockScalingY: lock,
      hasControls: !lock,
      hoverCursor: lock ? 'not-allowed' : null,
      erasable: !lock,
    });
  }, []);

  const syncPaperTransform = useCallback(() => {
    const c = fcRef.current; const paper = paperRef.current;
    if (!c || !paper) return;
    const vpt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
    const z = vpt[0];
    const g = live.current.gridSize * z;
    paper.style.backgroundSize = `${g}px ${g}px`;
    paper.style.backgroundPosition = `${vpt[4]}px ${vpt[5]}px`;
  }, []);

  const applyBackground = useCallback((bg) => {
    const paper = paperRef.current; if (!paper) return;
    const conf = BACKGROUNDS[bg] || BACKGROUNDS.light;
    paper.style.backgroundColor = conf.color || '#ffffff';
    paper.classList.toggle('cv-dots', conf.pattern === 'dots');
    paper.classList.toggle('cv-grid', conf.pattern === 'grid');
    syncPaperTransform();
  }, [syncPaperTransform]);

  const refreshZoom = useCallback(() => {
    store.getState().setZoom(fcRef.current.getZoom());
    syncPaperTransform();
  }, [store, syncPaperTransform]);

  const refreshCounts = useCallback(() => {
    const c = fcRef.current; if (!c) return;
    store.getState().setObjectCount(c.getObjects().length);
  }, [store]);

  // Build a style summary of the current selection for the right panel.
  const refreshSelection = useCallback(() => {
    const c = fcRef.current; if (!c) return;
    const active = c.getActiveObjects();
    if (!active.length) { store.getState().setSelection({ count: 0, kind: null, style: {} }); return; }
    const o = active[0];
    // Flowchart nodes are Group(shape, textbox): style reads come from the
    // inner shape, font reads from the inner textbox.
    const node = isNode(o);
    const sh = node ? o.item?.(0) : o;
    const tb = node ? nodeText(o) : null;
    const isText = node || o.type === 'i-text' || o.type === 'textbox' || o.type === 'text';
    const fontSrc = tb || o;
    store.getState().setSelection({
      count: active.length,
      kind: o.customType || o.type,
      locked: !!o.locked,
      style: {
        fill: typeof sh?.fill === 'string' ? sh.fill : '#000000',
        stroke: typeof sh?.stroke === 'string' ? sh.stroke : '',
        strokeWidth: sh?.strokeWidth || 0,
        opacity: o.opacity ?? 1,
        angle: Math.round(o.angle || 0),
        left: Math.round(o.left || 0),
        top: Math.round(o.top || 0),
        width: Math.round(o.getScaledWidth?.() || o.width || 0),
        height: Math.round(o.getScaledHeight?.() || o.height || 0),
        isText,
        isNode: node,
        fontSize: fontSrc.fontSize || TEXT_DEFAULTS.fontSize,
        fontFamily: fontSrc.fontFamily || TEXT_DEFAULTS.fontFamily,
        fontWeight: fontSrc.fontWeight || 'normal',
        textAlign: fontSrc.textAlign || 'left',
      },
    });
  }, [store]);

  // ── History ────────────────────────────────────────────────────────────────
  const snapshot = useCallback(() => {
    const c = fcRef.current; const h = history.current;
    if (!c || h.applying) return;
    const json = JSON.stringify(c.toObject(EXTRA_PROPS));
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(json);
    if (h.stack.length > MAX_HISTORY) h.stack.shift();
    h.index = h.stack.length - 1;
    store.getState().setHistoryFlags(h.index > 0, false);
    store.getState().setDirty(true);
    refreshCounts();
    // Notify collab layer of a committed local change (broadcast is debounced there).
    onChangeRef.current?.(json);
  }, [store, refreshCounts]);

  const applyHistory = useCallback(async (json) => {
    const c = fcRef.current; const h = history.current;
    h.applying = true;
    await c.loadFromJSON(json);
    c.requestRenderAll();
    h.applying = false;
    refreshCounts();
    refreshSelection();
  }, [refreshCounts, refreshSelection]);

  // ── Tool configuration (drawing mode / selectability / cursor) ───────────────
  const applyToolConfig = useCallback(() => {
    const c = fcRef.current; if (!c) return;
    const tool = live.current.tool;
    const isDraw = tool === 'pen' || tool === 'brush' || tool === 'eraser';
    c.isDrawingMode = isDraw;
    if (tool === 'eraser') {
      // Partial (Photoshop-style) erasing: strokes over erasable objects are
      // committed into a ClippingGroup clipPath on each hit object.
      const eb = new EraserBrush(c);
      eb.width = Math.max(8, live.current.brushWidth * 2);
      eb.on('end', (e) => {
        e.preventDefault();
        const detail = e.detail;
        eb.commit(detail).then(() => {
          c.requestRenderAll();
          if (detail?.targets?.length) snapshot();
        });
      });
      c.freeDrawingBrush = eb;
    } else if (isDraw) {
      const brush = new fabric.PencilBrush(c);
      brush.color = live.current.brushColor;
      brush.width = tool === 'pen' ? Math.max(1, Math.round(live.current.brushWidth / 2)) : live.current.brushWidth;
      c.freeDrawingBrush = brush;
    }
    const selectable = tool === 'select';
    c.selection = selectable;
    c.skipTargetFind = tool !== 'select';
    c.defaultCursor = tool === 'hand' ? 'grab'
      : tool === 'eraser' ? 'cell'
      : tool === 'select' ? 'default' : 'crosshair';
    // Locked objects stay selectable/evented so they can be clicked and
    // unlocked; applyLockState blocks movement/scaling/erasing instead.
    c.forEachObject((o) => { o.selectable = selectable; o.evented = selectable; applyLockState(o); });
    c.requestRenderAll();
  }, [applyLockState, snapshot]);

  // ── Object creation per tool ─────────────────────────────────────────────────
  const makeShape = useCallback((tool, p) => {
    const base = { left: p.x, top: p.y, originX: 'left', originY: 'top', ...SHAPE_DEFAULTS };
    switch (tool) {
      case 'rectangle': return withMeta(new fabric.Rect({ ...base, width: 1, height: 1, rx: 4, ry: 4 }), tool);
      case 'circle': return withMeta(new fabric.Ellipse({ ...base, rx: 1, ry: 1 }), tool);
      case 'triangle': return withMeta(new fabric.Triangle({ ...base, width: 1, height: 1 }), tool);
      case 'line': return withMeta(new fabric.Line([p.x, p.y, p.x, p.y], { stroke: SHAPE_DEFAULTS.stroke, strokeWidth: 3 }), tool);
      // 'arrow' is drafted as two free objects (line + head) in onDown/onMove —
      // mutating a Line inside a live Group doesn't re-render in Fabric v7 —
      // and grouped into the final arrow on mouse-up.
      case 'flowchart_process':
        return withMeta(new fabric.Rect({ ...base, width: 1, height: 1, fill: '#eff6ff', stroke: '#2563eb' }), tool);
      case 'flowchart_start':
        return withMeta(new fabric.Rect({ ...base, width: 1, height: 1, rx: 30, ry: 30, fill: '#ecfdf5', stroke: '#059669' }), tool);
      default: return null;
    }
  }, [withMeta]);

  // Flowchart node factory: Group(shape, centred wrapping Textbox). The group
  // serialises natively (children + subTargetCheck/interactive round-trip), so
  // no custom class registration is needed.
  const makeNodeGroup = useCallback((tool, { cx, cy, w, h, label, fill, stroke } = {}) => {
    const style = NODE_STYLES[tool] || NODE_STYLES.flowchart_process;
    const common = {
      fill: fill || style.fill, stroke: stroke || style.stroke, strokeWidth: 2,
      originX: 'center', originY: 'center', left: cx, top: cy,
    };
    let shape;
    if (tool === 'flowchart_decision') shape = new fabric.Polygon(diamondPoints(w, h), common);
    else if (tool === 'flowchart_start') {
      const rr = Math.min(w, h) / 2;
      shape = new fabric.Rect({ ...common, width: w, height: h, rx: rr, ry: rr });
    } else shape = new fabric.Rect({ ...common, width: w, height: h });
    const tb = new fabric.Textbox(label ?? style.label, {
      width: nodeTextWidth(tool, w), fontSize: 16, fontFamily: TEXT_DEFAULTS.fontFamily,
      fill: '#0f172a', textAlign: 'center', originX: 'center', originY: 'center',
      left: cx, top: cy, splitByGrapheme: false,
    });
    // subTargetCheck lets dblclick find the group; `interactive` stays false so
    // the node moves as one unit — it's flipped on temporarily for text editing.
    const g = new fabric.Group([shape, tb], { subTargetCheck: true, interactive: false });
    return withMeta(g, tool);
  }, [withMeta]);

  // Grow a node's shape so the wrapped label always fits vertically; never
  // shrinks below the user-drawn size. Called while typing and on font changes.
  const autoGrowNode = useCallback((g) => {
    if (!isNode(g)) return;
    const shape = g.item?.(0); const tb = nodeText(g);
    if (!shape || !tb) return;
    // The diamond's inscribed region is roughly half its bounds, so its label
    // needs extra headroom.
    const factor = shape.type === 'polygon' ? 1.9 : 1;
    const needH = (tb.height + NODE_PAD_Y * 2) * factor;
    const shapeH = shape.getScaledHeight();
    if (needH <= shapeH + 0.5) return;
    const center = g.getCenterPoint();
    if (shape.type === 'polygon') {
      // Polygon width/height don't rescale points — grow via scale.
      const k = needH / shapeH;
      shape.set({ scaleX: (shape.scaleX || 1) * k, scaleY: (shape.scaleY || 1) * k });
    } else {
      shape.set({ height: needH });
      if (g.customType === 'flowchart_start') {
        const rr = Math.min(shape.width, needH) / 2;
        shape.set({ rx: rr, ry: rr });
      }
    }
    g.triggerLayout?.();
    g.setCoords();
    g.setPositionByOrigin(center, 'center', 'center');
    g.setCoords();
  }, []);

  // After a user resize (scale handles), bake the group's scale into real
  // dimensions and re-wrap the label to the new width instead of stretching it.
  const normalizeNodeScale = useCallback((g) => {
    const shape = g.item?.(0); const tb = nodeText(g);
    if (!shape || !tb) return;
    const sx = g.scaleX || 1, sy = g.scaleY || 1;
    if (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001) return;
    const newW = shape.getScaledWidth() * sx;
    const newH = shape.getScaledHeight() * sy;
    const center = g.getCenterPoint();
    g.set({ scaleX: 1, scaleY: 1 });
    if (shape.type === 'polygon') {
      shape.set({ scaleX: newW / shape.width, scaleY: newH / shape.height });
    } else {
      shape.set({ width: newW, height: newH, scaleX: 1, scaleY: 1 });
      if (g.customType === 'flowchart_start') {
        const rr = Math.min(newW, newH) / 2;
        shape.set({ rx: rr, ry: rr });
      }
    }
    shape.set({ left: 0, top: 0 });
    tb.set({ width: nodeTextWidth(g.customType, newW), scaleX: 1, scaleY: 1, left: 0, top: 0 });
    g.triggerLayout?.();
    g.setCoords();
    g.setPositionByOrigin(center, 'center', 'center');
    g.setCoords();
    autoGrowNode(g);
  }, [autoGrowNode]);

  const placeFixed = useCallback((tool, p) => {
    const c = fcRef.current;
    let obj = null;
    if (tool === 'star') obj = withMeta(new fabric.Polygon(starPoints(), { ...SHAPE_DEFAULTS, left: p.x, top: p.y, originX: 'center', originY: 'center' }), tool);
    else if (tool === 'polygon') obj = withMeta(new fabric.Polygon(polygonPoints(), { ...SHAPE_DEFAULTS, left: p.x, top: p.y, originX: 'center', originY: 'center' }), tool);
    else if (tool === 'flowchart_decision') obj = makeNodeGroup(tool, { cx: p.x, cy: p.y, w: 150, h: 92 });
    else if (tool === 'text') {
      obj = withMeta(new fabric.IText('Text', { ...TEXT_DEFAULTS, left: p.x, top: p.y, originX: 'left', originY: 'top' }), tool);
    } else if (tool === 'sticky') {
      const col = STICKY_COLORS[live.current.stickyColorIndex % STICKY_COLORS.length];
      obj = new fabric.Textbox('Note', {
        left: p.x, top: p.y, width: 200, fontSize: 18, fontFamily: TEXT_DEFAULTS.fontFamily,
        fill: col.fg, backgroundColor: col.bg, padding: 14, textAlign: 'left',
        originX: 'left', originY: 'top', splitByGrapheme: false,
      });
      withMeta(obj, 'sticky'); obj.set('stickyFg', col.fg);
    }
    if (!obj) return;
    c.add(obj);
    c.setActiveObject(obj);
    store.getState().setActiveTool('select');
    if (obj.type === 'i-text') obj.enterEditing?.();
    c.requestRenderAll();
    snapshot();
    refreshSelection();
  }, [withMeta, makeNodeGroup, store, snapshot, refreshSelection]);

  // ── Starter templates (returns ready-to-add Fabric objects) ──────────────────
  const templateObjects = useCallback((name, cx, cy) => {
    const out = [];
    const reg = (o, type) => { withMeta(o, type || 'shape'); out.push(o); return o; };
    const label = (x, y, str, size = 16, color = '#0f172a') =>
      reg(new fabric.IText(str, { left: x, top: y, fontSize: size, fontFamily: TEXT_DEFAULTS.fontFamily, fill: color, originX: 'center', originY: 'center', textAlign: 'center' }), 'text');
    const arrow = (x1, y1, x2, y2) => {
      const line = new fabric.Line([x1, y1, x2, y2], { stroke: '#475569', strokeWidth: 2 });
      const ang = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI + 90;
      const head = new fabric.Triangle({ left: x2, top: y2, width: 13, height: 13, fill: '#475569', originX: 'center', originY: 'center', angle: ang });
      return reg(new fabric.Group([line, head]), 'arrow');
    };

    if (name === 'flowchart') {
      const w = 150, h = 54, col = cx, y0 = cy - 200;
      out.push(makeNodeGroup('flowchart_start', { cx: col, cy: y0, w, h, label: 'Start' }));
      arrow(col, y0 + h / 2, col, y0 + 100 - h / 2);
      const yP = y0 + 100;
      out.push(makeNodeGroup('flowchart_process', { cx: col, cy: yP, w, h, label: 'Process' }));
      arrow(col, yP + h / 2, col, yP + 75);
      const yD = yP + 120;
      out.push(makeNodeGroup('flowchart_decision', { cx: col, cy: yD, w: 160, h: 96, label: 'Decision?' }));
      const yE = yD + 140;
      arrow(col - 30, yD + 48, col - 150, yE - 27);
      arrow(col + 30, yD + 48, col + 150, yE - 27);
      label(col - 110, yD + 80, 'No', 13, '#b91c1c');
      label(col + 110, yD + 80, 'Yes', 13, '#15803d');
      out.push(makeNodeGroup('flowchart_start', { cx: col - 150, cy: yE, w, h, label: 'End A', fill: '#fef2f2', stroke: '#dc2626' }));
      out.push(makeNodeGroup('flowchart_start', { cx: col + 150, cy: yE, w, h, label: 'End B' }));
    } else if (name === 'kanban') {
      const colW = 210, colH = 380, gap = 24;
      const cols = [{ t: 'To Do', c: '#f1f5f9' }, { t: 'In Progress', c: '#eff6ff' }, { t: 'Done', c: '#ecfdf5' }];
      const x0 = cx - (colW * 3 + gap * 2) / 2, y0 = cy - colH / 2;
      cols.forEach((cd, i) => {
        const x = x0 + i * (colW + gap);
        reg(new fabric.Rect({ left: x, top: y0, width: colW, height: colH, rx: 12, ry: 12, fill: cd.c, stroke: '#cbd5e1', strokeWidth: 1, originX: 'left', originY: 'top' }), 'shape');
        label(x + colW / 2, y0 + 26, cd.t, 16);
        for (let k = 0; k < 2; k++) {
          const cardY = y0 + 56 + k * 70;
          reg(new fabric.Rect({ left: x + 14, top: cardY, width: colW - 28, height: 54, rx: 8, ry: 8, fill: '#ffffff', stroke: '#e2e8f0', strokeWidth: 1, originX: 'left', originY: 'top' }), 'shape');
          label(x + colW / 2, cardY + 27, 'Card', 13, '#64748b');
        }
      });
    } else if (name === 'mindmap') {
      reg(new fabric.Ellipse({ left: cx, top: cy, rx: 82, ry: 46, fill: '#fff7ed', stroke: '#f54b01', strokeWidth: 2, originX: 'center', originY: 'center' }), 'shape');
      const spokes = [[-260, -150], [260, -150], [-260, 150], [260, 150]];
      spokes.forEach(([dx, dy], i) => {
        const nx = cx + dx, ny = cy + dy;
        reg(new fabric.Line([cx + Math.sign(dx) * 82, cy + Math.sign(dy) * 30, nx, ny], { stroke: '#94a3b8', strokeWidth: 2 }), 'shape');
        reg(new fabric.Ellipse({ left: nx, top: ny, rx: 66, ry: 36, fill: '#eff6ff', stroke: '#2563eb', strokeWidth: 2, originX: 'center', originY: 'center' }), 'shape');
        label(nx, ny, `Topic ${i + 1}`, 14, '#1e40af');
      });
      label(cx, cy, 'Main idea', 17, '#9a3412');
    } else if (name === 'retro') {
      // Sprint retro — Start / Stop / Continue columns.
      const colW = 220, colH = 400, gap = 24;
      const cols = [
        { t: 'Start', c: '#ecfdf5', s: '#059669' },
        { t: 'Stop', c: '#fef2f2', s: '#dc2626' },
        { t: 'Continue', c: '#eff6ff', s: '#2563eb' },
      ];
      const x0 = cx - (colW * 3 + gap * 2) / 2, y0 = cy - colH / 2;
      cols.forEach((cd, i) => {
        const x = x0 + i * (colW + gap);
        reg(new fabric.Rect({ left: x, top: y0, width: colW, height: colH, rx: 12, ry: 12, fill: cd.c, stroke: cd.s, strokeWidth: 1.5, originX: 'left', originY: 'top' }), 'shape');
        label(x + colW / 2, y0 + 26, cd.t, 16, cd.s);
      });
    } else if (name === 'swot') {
      // SWOT — 2×2 grid.
      const cellW = 260, cellH = 200, gap = 16;
      const cells = [
        { t: 'Strengths', c: '#ecfdf5', s: '#059669' },
        { t: 'Weaknesses', c: '#fef2f2', s: '#dc2626' },
        { t: 'Opportunities', c: '#eff6ff', s: '#2563eb' },
        { t: 'Threats', c: '#fffbeb', s: '#d97706' },
      ];
      const x0 = cx - cellW - gap / 2, y0 = cy - cellH - gap / 2;
      cells.forEach((cd, i) => {
        const x = x0 + (i % 2) * (cellW + gap);
        const y = y0 + Math.floor(i / 2) * (cellH + gap);
        reg(new fabric.Rect({ left: x, top: y, width: cellW, height: cellH, rx: 10, ry: 10, fill: cd.c, stroke: cd.s, strokeWidth: 1.5, originX: 'left', originY: 'top' }), 'shape');
        label(x + cellW / 2, y + 24, cd.t, 16, cd.s);
      });
    } else if (name === 'timeline') {
      // Timeline — baseline arrow + alternating milestone markers.
      const span = 720, n = 4, x0 = cx - span / 2, step = span / (n - 1);
      arrow(x0 - 30, cy, x0 + span + 30, cy);
      for (let i = 0; i < n; i++) {
        const x = x0 + i * step;
        reg(new fabric.Circle({ left: x, top: cy, radius: 10, fill: '#f54b01', stroke: '#ffffff', strokeWidth: 2, originX: 'center', originY: 'center' }), 'shape');
        const up = i % 2 === 0;
        const my = cy + (up ? -80 : 80);
        reg(new fabric.Line([x, cy, x, my + (up ? 22 : -22)], { stroke: '#cbd5e1', strokeWidth: 1.5 }), 'shape');
        reg(new fabric.Rect({ left: x, top: my, width: 150, height: 44, rx: 8, ry: 8, fill: '#ffffff', stroke: '#e2e8f0', strokeWidth: 1, originX: 'center', originY: 'center' }), 'shape');
        label(x, my - 6, `Milestone ${i + 1}`, 13);
        label(x, my + 12, 'Date', 11, '#94a3b8');
      }
    } else if (name === 'orgchart') {
      // Org chart — CEO over three reports.
      const w = 160, h = 56;
      out.push(makeNodeGroup('flowchart_process', { cx, cy: cy - 140, w, h, label: 'CEO', fill: '#fff7ed', stroke: '#f54b01' }));
      const reports = [-220, 0, 220];
      reports.forEach((dx, i) => {
        const nx = cx + dx, ny = cy + 40;
        arrow(cx, cy - 140 + h / 2, nx, ny - h / 2);
        out.push(makeNodeGroup('flowchart_process', { cx: nx, cy: ny, w, h, label: `Lead ${i + 1}` }));
      });
    } else if (name === 'journey') {
      // User journey — 5 stages with emotion row.
      const stages = ['Awareness', 'Consideration', 'Decision', 'Onboarding', 'Retention'];
      const cellW = 170, gap = 12, cellH = 60;
      const x0 = cx - (cellW * stages.length + gap * (stages.length - 1)) / 2, y0 = cy - 90;
      stages.forEach((s, i) => {
        const x = x0 + i * (cellW + gap);
        reg(new fabric.Rect({ left: x, top: y0, width: cellW, height: cellH, rx: 8, ry: 8, fill: '#eff6ff', stroke: '#2563eb', strokeWidth: 1.5, originX: 'left', originY: 'top' }), 'shape');
        label(x + cellW / 2, y0 + cellH / 2, s, 14, '#1e40af');
        reg(new fabric.Rect({ left: x, top: y0 + cellH + 16, width: cellW, height: 100, rx: 8, ry: 8, fill: '#ffffff', stroke: '#e2e8f0', strokeWidth: 1, originX: 'left', originY: 'top' }), 'shape');
        label(x + cellW / 2, y0 + cellH + 40, 'Actions', 12, '#64748b');
      });
    }
    return out;
  }, [withMeta, makeNodeGroup]);

  // ── Pinch / two-finger gesture (touch) ───────────────────────────────────────
  const beginGesture = useCallback(() => {
    const c = fcRef.current;
    const [a, b] = [...pointers.current.values()];
    gesture.current = {
      startDist: dist(a, b), startZoom: c.getZoom(), prevMid: mid(a, b),
      prev: { selection: c.selection, drawing: c.isDrawingMode, skip: c.skipTargetFind },
    };
    c.selection = false; c.isDrawingMode = false; c.skipTargetFind = true;
    c.discardActiveObject(); c.requestRenderAll();
    draft.current = null; panning.current = null;
  }, []);

  const endGesture = useCallback(() => {
    gesture.current = null;
    // Restore tool config on the next tick so the lifting finger's synthetic
    // click doesn't immediately select/draw.
    setTimeout(() => applyToolConfig(), 0);
  }, [applyToolConfig]);

  // ── Mount ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const el = canvasElRef.current; const wrap = wrapRef.current;
    if (!el || !wrap) return;

    const c = new fabric.Canvas(el, {
      width: wrap.clientWidth, height: wrap.clientHeight,
      backgroundColor: '', preserveObjectStacking: true,
      selection: true, enableRetinaScaling: true, fireRightClick: true, stopContextMenu: true,
    });
    fcRef.current = c;
    paperRef.current = c.wrapperEl;
    if (paperRef.current) paperRef.current.classList.add('cv-paper');
    applyBackground(live.current.background);

    // ── Pointer handlers (selection / shape draw / pan / eraser) ──────────────
    const onDown = (opt) => {
      const e = opt.e;
      if (gesture.current) return;
      const tool = live.current.tool;
      // Pan: hand tool, space-drag, or middle mouse
      if (tool === 'hand' || live.current.spaceHeld || e.button === 1) {
        panning.current = { x: e.clientX ?? (e.touches?.[0]?.clientX), y: e.clientY ?? (e.touches?.[0]?.clientY) };
        c.setCursor('grabbing');
        return;
      }
      if (['star', 'polygon', 'text', 'sticky', 'flowchart_decision'].includes(tool)) { placeFixed(tool, scenePoint(e)); return; }
      if (['rectangle', 'circle', 'triangle', 'line', 'arrow', 'flowchart_start', 'flowchart_process'].includes(tool)) {
        if (c.getObjects().length >= MAX_OBJECTS) { toast.error(`Max ${MAX_OBJECTS} objects`); return; }
        const p = scenePoint(e);
        startPt.current = p;
        history.current.applying = true; // suppress add-snapshot; finalise on up
        if (tool === 'arrow') {
          // Draft the arrow as two free objects — a Line mutated inside a live
          // Group doesn't re-render in Fabric v7. Grouped on mouse-up.
          const line = new fabric.Line([p.x, p.y, p.x, p.y], { stroke: SHAPE_DEFAULTS.stroke, strokeWidth: 3, selectable: false, evented: false });
          const head = new fabric.Triangle({ left: p.x, top: p.y, width: 16, height: 16, fill: SHAPE_DEFAULTS.stroke, originX: 'center', originY: 'center', angle: 90, selectable: false, evented: false });
          c.add(line); c.add(head);
          draft.current = { arrowParts: { line, head } };
        } else {
          const obj = makeShape(tool, p);
          c.add(obj);
          draft.current = obj;
        }
      }
    };

    const onMove = (opt) => {
      const e = opt.e;
      if (gesture.current) return;
      if (panning.current) {
        const x = e.clientX ?? e.touches?.[0]?.clientX, y = e.clientY ?? e.touches?.[0]?.clientY;
        const vpt = c.viewportTransform;
        vpt[4] += x - panning.current.x; vpt[5] += y - panning.current.y;
        c.setViewportTransform(vpt);
        panning.current = { x, y };
        syncPaperTransform();
        return;
      }
      const d = draft.current; if (!d || !startPt.current) return;
      const p = scenePoint(e);
      const s = startPt.current;
      const tool = live.current.tool;
      if (d.arrowParts) {
        const { line, head } = d.arrowParts;
        line.set({ x1: s.x, y1: s.y, x2: p.x, y2: p.y });
        head.set({
          left: p.x, top: p.y,
          angle: (Math.atan2(p.y - s.y, p.x - s.x) * 180) / Math.PI + 90,
        });
        line.setCoords(); head.setCoords();
        c.requestRenderAll();
        return;
      }
      if (tool === 'line') {
        d.set({ x2: p.x, y2: p.y });
      } else {
        const w = Math.abs(p.x - s.x), h = Math.abs(p.y - s.y);
        const left = Math.min(p.x, s.x), top = Math.min(p.y, s.y);
        if (tool === 'circle') d.set({ left, top, rx: w / 2, ry: h / 2 });
        else {
          d.set({ left, top, width: Math.max(1, w), height: Math.max(1, h) });
          if (tool === 'flowchart_start') { const rr = Math.min(w, h) / 2; d.set({ rx: rr, ry: rr }); }
        }
      }
      d.setCoords();
      c.requestRenderAll();
    };

    const onUp = () => {
      if (panning.current) { panning.current = null; applyToolConfig(); return; }
      const d = draft.current;
      if (d) {
        history.current.applying = false;
        // Arrow draft: group the free line + head into the final arrow object.
        if (d.arrowParts) {
          const { line, head } = d.arrowParts;
          c.remove(line); c.remove(head);
          const len = Math.hypot((line.x2 ?? 0) - (line.x1 ?? 0), (line.y2 ?? 0) - (line.y1 ?? 0));
          if (len >= 4) {
            line.set({ selectable: true, evented: true });
            head.set({ selectable: true, evented: true });
            const grp = withMeta(new fabric.Group([line, head]), 'arrow');
            c.add(grp);
            c.setActiveObject(grp);
            store.getState().setActiveTool('select');
            snapshot(); refreshSelection();
          }
          draft.current = null; startPt.current = null;
          c.requestRenderAll();
          return;
        }
        // Drop zero-size accidental drags; otherwise finalise.
        const tooSmall = (d.width < 4 && d.height < 4 && d.type !== 'line' && d.type !== 'group');
        const nodeTool = d.customType === 'flowchart_start' || d.customType === 'flowchart_process';
        if (tooSmall && !nodeTool) { fcRef.current.remove(d); }
        else if (nodeTool) {
          // Replace the bare draft rect with a text-carrying node group. A
          // plain click (no drag) still yields a sensibly sized node.
          const w = tooSmall ? 150 : Math.max(d.width, 90);
          const h = tooSmall ? 54 : Math.max(d.height, 44);
          const cx = d.left + d.width / 2, cy = d.top + d.height / 2;
          c.remove(d);
          const node = makeNodeGroup(d.customType, { cx, cy, w, h });
          c.add(node);
          c.setActiveObject(node);
          store.getState().setActiveTool('select');
          snapshot(); refreshSelection();
        }
        else {
          fcRef.current.setActiveObject(d);
          store.getState().setActiveTool('select');
          snapshot(); refreshSelection();
        }
        draft.current = null; startPt.current = null;
        fcRef.current.requestRenderAll();
      }
    };

    const onDblClick = (opt) => {
      const t = opt.target;
      if (t && isNode(t) && !t.locked) {
        // Enter label editing inside the node group. Nested text only accepts
        // editing interaction while its group is `interactive`, so flip it on
        // for the editing session and restore afterwards.
        const tb = nodeText(t);
        if (!tb) return;
        t.set('interactive', true);
        c.setActiveObject(tb);
        tb.enterEditing?.(); tb.selectAll?.();
        tb.once('editing:exited', () => {
          t.set('interactive', false);
          autoGrowNode(t);
          c.setActiveObject(t);
          c.requestRenderAll();
          snapshot(); refreshSelection();
        });
        c.requestRenderAll();
        return;
      }
      if (t && (t.type === 'textbox' || t.type === 'i-text')) { t.enterEditing?.(); t.selectAll?.(); }
    };

    // Re-fit the node shape around its label while the user types.
    const onTextChanged = (opt) => {
      const t = opt.target;
      if (t?.group && isNode(t.group)) { autoGrowNode(t.group); c.requestRenderAll(); }
    };

    const onWheel = (opt) => {
      const e = opt.e; e.preventDefault(); e.stopPropagation();
      if (e.ctrlKey || e.metaKey) {
        const z = clamp(c.getZoom() * 0.999 ** e.deltaY);
        c.zoomToPoint(new fabric.Point(e.offsetX, e.offsetY), z);
        refreshZoom();
      } else {
        const vpt = c.viewportTransform;
        vpt[4] -= e.deltaX; vpt[5] -= e.deltaY;
        c.setViewportTransform(vpt);
        syncPaperTransform();
      }
    };

    // Snap-to-grid while moving.
    const onMoving = (opt) => {
      if (!live.current.snapEnabled) return;
      const g = live.current.gridSize; const o = opt.target;
      o.set({ left: Math.round(o.left / g) * g, top: Math.round(o.top / g) * g });
    };

    const tagPath = (opt) => {
      if (live.current.tool === 'eraser') return; // eraser commits + snapshots itself
      if (opt.path) withMeta(opt.path, 'path');
      snapshot();
    };
    const onModified = (opt) => {
      const t = opt?.target;
      // Bake resize scale into node dimensions so the label re-wraps.
      if (t && isNode(t)) normalizeNodeScale(t);
      snapshot(); refreshSelection();
    };
    const onSel = () => {
      // Locked objects can be click-selected individually (to reach Unlock) but
      // are stripped from multi-selections so a marquee drag can't move them.
      const a = c.getActiveObject();
      if (a?.type === 'activeselection' && a.getObjects().length > 1) {
        const lockedKids = a.getObjects().filter((o) => o.locked);
        if (lockedKids.length) { lockedKids.forEach((o) => a.remove(o)); c.requestRenderAll(); }
      }
      refreshSelection();
    };
    const onSelCleared = () => store.getState().setSelection({ count: 0, kind: null, style: {} });

    c.on('mouse:down', onDown);
    c.on('mouse:move', onMove);
    c.on('mouse:up', onUp);
    c.on('mouse:dblclick', onDblClick);
    c.on('text:changed', onTextChanged);
    c.on('mouse:wheel', onWheel);
    c.on('object:moving', onMoving);
    c.on('path:created', tagPath);
    c.on('object:modified', onModified);
    c.on('object:removed', () => refreshCounts());
    c.on('selection:created', onSel);
    c.on('selection:updated', onSel);
    c.on('selection:cleared', onSelCleared);

    // Right-click → context menu (only over a target, in select mode).
    const onContext = (e) => {
      e.preventDefault();
      const target = c.findTarget?.(e);
      if (target) { c.setActiveObject(target); c.requestRenderAll(); refreshSelection(); }
      store.getState().setContextMenu({ x: e.clientX, y: e.clientY, hasTarget: !!target });
    };
    c.upperCanvasEl.addEventListener('contextmenu', onContext);

    // ── Touch gatekeeper + two-finger pinch/pan (capture phase) ───────────────
    // Runs on wrapperEl BEFORE Fabric's listeners on the child upperCanvasEl, so
    // stopPropagation() here hides a touch from Fabric entirely (needed because
    // freehand pen/brush is drawn by Fabric internally, not via our onDown).
    const paper = c.wrapperEl;
    let rawPan = null; // {x,y} while a swallowed single finger is panning

    // First stylus contact → remember it and (once) turn finger drawing off so a
    // resting palm can't ink. The user can flip it back on in the panel.
    const maybeAutoDisableFinger = () => {
      const s = store.getState();
      if (s.stylusSeen) return;
      s.setStylusSeen(true);
      if (s.fingerDrawing) {
        s.setFingerDrawing(false);
        toast('Apple Pencil detected — finger drawing turned off', {
          description: 'Re-enable it anytime in the canvas panel.',
        });
      }
    };

    const onPD = (e) => {
      if (e.pointerType === 'mouse') return;          // mouse → let Fabric handle
      if (e.pointerType === 'pen') {                  // stylus draws; never gestures
        penDown.current = true;
        rawPan = null;
        pointers.current.clear();                     // drop any palm that landed first
        if (gesture.current) endGesture();
        maybeAutoDisableFinger();
        return;
      }
      // ── touch ──
      if (penDown.current) {                          // palm rejection: the pen owns the surface
        e.preventDefault(); e.stopPropagation();
        return;
      }
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size >= 2) {               // two fingers → pinch/pan
        rawPan = null;
        beginGesture();
        return;
      }
      // single finger + finger-drawing off on a creation tool → don't ink; pan instead
      if (!live.current.fingerDrawing && DRAW_TOOLS.has(live.current.tool)) {
        e.preventDefault(); e.stopPropagation();
        rawPan = { x: e.clientX, y: e.clientY };
      }
    };

    const onPM = (e) => {
      if (e.pointerType === 'pen') return;
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (gesture.current && pointers.current.size >= 2) {
        e.preventDefault(); e.stopPropagation();
        const [a, b] = [...pointers.current.values()];
        const rect = paper.getBoundingClientRect();
        const nMid = mid(a, b);
        const z = clamp(gesture.current.startZoom * (dist(a, b) / gesture.current.startDist));
        c.zoomToPoint(new fabric.Point(nMid.x - rect.left, nMid.y - rect.top), z);
        const vpt = c.viewportTransform;
        vpt[4] += nMid.x - gesture.current.prevMid.x;
        vpt[5] += nMid.y - gesture.current.prevMid.y;
        c.setViewportTransform(vpt);
        gesture.current.prevMid = nMid;
        refreshZoom();
        return;
      }
      if (rawPan && !gesture.current && pointers.current.size === 1) {
        e.preventDefault(); e.stopPropagation();
        const vpt = c.viewportTransform;
        vpt[4] += e.clientX - rawPan.x; vpt[5] += e.clientY - rawPan.y;
        c.setViewportTransform(vpt);
        rawPan = { x: e.clientX, y: e.clientY };
        syncPaperTransform();
      }
    };

    const onPU = (e) => {
      if (e.pointerType === 'pen') { penDown.current = false; return; }
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2 && gesture.current) endGesture();
      if (pointers.current.size === 0) rawPan = null;
    };
    paper.addEventListener('pointerdown', onPD, { capture: true });
    paper.addEventListener('pointermove', onPM, { capture: true, passive: false });
    paper.addEventListener('pointerup', onPU, { capture: true });
    paper.addEventListener('pointercancel', onPU, { capture: true });

    // ── Resize ─────────────────────────────────────────────────────────────────
    const ro = new ResizeObserver(() => {
      c.setDimensions({ width: wrap.clientWidth, height: wrap.clientHeight });
      c.requestRenderAll(); syncPaperTransform();
    });
    ro.observe(wrap);

    applyToolConfig();
    // Seed history with the empty canvas.
    history.current.stack = [JSON.stringify(c.toObject(EXTRA_PROPS))];
    history.current.index = 0;
    onReady?.();

    return () => {
      ro.disconnect();
      c.upperCanvasEl?.removeEventListener('contextmenu', onContext);
      paper.removeEventListener('pointerdown', onPD, { capture: true });
      paper.removeEventListener('pointermove', onPM, { capture: true });
      paper.removeEventListener('pointerup', onPU, { capture: true });
      paper.removeEventListener('pointercancel', onPU, { capture: true });
      c.dispose();
      fcRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Sync store → live ref (no rebinding of canvas handlers) ───────────────────
  const { activeTool, brushColor, brushWidth, snapEnabled, gridSize, stickyColorIndex, background, fingerDrawing } =
    useCanvasStore();
  useEffect(() => {
    live.current = { ...live.current, tool: activeTool, brushColor, brushWidth, snapEnabled, gridSize, stickyColorIndex, background, fingerDrawing };
    if (fcRef.current) applyToolConfig();
  }, [activeTool, brushColor, brushWidth, snapEnabled, gridSize, stickyColorIndex, background, fingerDrawing, applyToolConfig]);
  useEffect(() => { if (fcRef.current) applyBackground(background); }, [background, applyBackground]);

  // ── Space-to-pan (held) ───────────────────────────────────────────────────────
  useEffect(() => {
    const isTyping = () => {
      const o = fcRef.current?.getActiveObject();
      return o && (o.isEditing);
    };
    const kd = (e) => { if (e.code === 'Space' && !isTyping()) { live.current.spaceHeld = true; if (fcRef.current) fcRef.current.defaultCursor = 'grab'; } };
    const ku = (e) => { if (e.code === 'Space') { live.current.spaceHeld = false; applyToolConfig(); } };
    window.addEventListener('keydown', kd);
    window.addEventListener('keyup', ku);
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); };
  }, [applyToolConfig]);

  // ── Imperative handle ─────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    getJSON: () => JSON.stringify(fcRef.current.toObject(EXTRA_PROPS)),
    isEmpty: () => fcRef.current.getObjects().length === 0,
    objectCount: () => fcRef.current.getObjects().length,

    loadJSON: async (json) => {
      if (!json) {
        // Empty board — wipe any existing scene (e.g. when switching from a
        // populated canvas to a blank one) and reset history to the empty state.
        const c = fcRef.current;
        c.getObjects().slice().forEach((o) => c.remove(o));
        c.discardActiveObject();
        c.requestRenderAll();
        history.current.stack = [JSON.stringify(c.toObject(EXTRA_PROPS))];
        history.current.index = 0;
        store.getState().setHistoryFlags(false, false);
        store.getState().setDirty(false);
        refreshCounts(); refreshSelection(); applyToolConfig();
        return;
      }
      const data = typeof json === 'string' ? json : JSON.stringify(json);
      history.current.applying = true;
      await fcRef.current.loadFromJSON(data);
      fcRef.current.requestRenderAll();
      history.current.applying = false;
      history.current.stack = [data]; history.current.index = 0;
      store.getState().setHistoryFlags(false, false);
      store.getState().setDirty(false);
      refreshCounts(); applyToolConfig();
    },

    // Apply a peer's board state during live collaboration. Skips while the
    // local user is mid-drag or editing text so we never yank work in progress.
    // Preserves the current viewport (loadFromJSON doesn't touch it). Returns
    // whether the update was applied.
    applyRemoteJSON: async (json) => {
      const c = fcRef.current; if (!c || !json) return false;
      const editing = c.getObjects().some((o) => o.isEditing);
      if (c._currentTransform || editing) return false;
      const data = typeof json === 'string' ? json : JSON.stringify(json);
      if (data === JSON.stringify(c.toObject(EXTRA_PROPS))) return true; // no-op / echo
      history.current.applying = true;
      await c.loadFromJSON(data);
      c.requestRenderAll();
      history.current.applying = false;
      refreshCounts(); refreshSelection();
      return true;
    },

    undo: async () => {
      const h = history.current; if (h.index <= 0) return;
      h.index -= 1; await applyHistory(h.stack[h.index]);
      store.getState().setHistoryFlags(h.index > 0, h.index < h.stack.length - 1);
      store.getState().setDirty(true);
    },
    redo: async () => {
      const h = history.current; if (h.index >= h.stack.length - 1) return;
      h.index += 1; await applyHistory(h.stack[h.index]);
      store.getState().setHistoryFlags(h.index > 0, h.index < h.stack.length - 1);
      store.getState().setDirty(true);
    },

    deleteSelected: () => {
      const c = fcRef.current; const objs = c.getActiveObjects().filter((o) => !o.locked);
      if (!objs.length) return;
      objs.forEach((o) => c.remove(o));
      c.discardActiveObject(); c.requestRenderAll(); snapshot(); refreshSelection();
    },
    duplicateSelected: async () => {
      const c = fcRef.current; const o = c.getActiveObject(); if (!o) return;
      const cl = await o.clone(EXTRA_PROPS);
      cl.set({ left: (o.left || 0) + 20, top: (o.top || 0) + 20, id: uid() });
      c.add(cl); c.setActiveObject(cl); c.requestRenderAll(); snapshot(); refreshSelection();
    },
    copySelected: async () => {
      const o = fcRef.current.getActiveObject(); if (!o) return;
      clipboard.current = await o.clone(EXTRA_PROPS);
      store.getState().setHasClipboard(true);
    },
    paste: async () => {
      if (!clipboard.current) return;
      const c = fcRef.current; const cl = await clipboard.current.clone(EXTRA_PROPS);
      cl.set({ left: (cl.left || 0) + 24, top: (cl.top || 0) + 24, id: uid() });
      c.add(cl); c.setActiveObject(cl); c.requestRenderAll(); snapshot(); refreshSelection();
    },

    bringToFront: () => { const c = fcRef.current; const o = c.getActiveObject(); if (o) { c.bringObjectToFront(o); c.requestRenderAll(); snapshot(); } },
    sendToBack: () => { const c = fcRef.current; const o = c.getActiveObject(); if (o) { c.sendObjectToBack(o); c.requestRenderAll(); snapshot(); } },

    toggleLockSelected: () => {
      const c = fcRef.current; const objs = c.getActiveObjects(); if (!objs.length) return;
      const lock = !objs[0].locked;
      // Locked objects stay selectable so they can be clicked and unlocked —
      // applyLockState blocks movement/scaling/rotation/erasing instead.
      // Keep the selection alive so Unlock stays one click away.
      objs.forEach((o) => { o.set({ locked: lock }); applyLockState(o); o.setCoords(); });
      c.requestRenderAll(); snapshot(); refreshSelection();
    },

    groupSelected: () => {
      const c = fcRef.current; const a = c.getActiveObject();
      if (!a || a.type !== 'activeselection') return;
      try {
        const objs = a.getObjects();
        c.discardActiveObject();
        objs.forEach((o) => c.remove(o));
        const grp = withMeta(new fabric.Group(objs), 'group');
        c.add(grp); c.setActiveObject(grp); c.requestRenderAll(); snapshot(); refreshSelection();
      } catch { toast.error('Could not group'); }
    },
    ungroupSelected: () => {
      const c = fcRef.current; const g = c.getActiveObject();
      if (!g || g.type !== 'group') return;
      try {
        const items = g.removeAll();
        c.remove(g);
        items.forEach((o) => { o.set({ selectable: true, evented: true }); c.add(o); });
        c.requestRenderAll(); snapshot(); refreshSelection();
      } catch { toast.error('Could not ungroup'); }
    },

    selectAll: () => {
      const c = fcRef.current; const objs = c.getObjects().filter((o) => o.selectable !== false && !o.locked);
      if (!objs.length) return;
      c.discardActiveObject();
      const sel = new fabric.ActiveSelection(objs, { canvas: c });
      c.setActiveObject(sel); c.requestRenderAll(); refreshSelection();
    },
    discardSelection: () => { fcRef.current.discardActiveObject(); fcRef.current.requestRenderAll(); refreshSelection(); },

    applyActiveStyle: (patch) => {
      const c = fcRef.current;
      // Locked objects can be selected (to unlock) but never restyled.
      const objs = c.getActiveObjects().filter((o) => !o.locked);
      if (!objs.length) return;
      const FONT_KEYS = ['fontSize', 'fontFamily', 'fontWeight', 'textAlign'];
      objs.forEach((o) => {
        if (isNode(o)) {
          // Route font props to the inner label, shape props to the inner
          // shape; position/opacity stay on the group.
          const shape = o.item?.(0); const tb = nodeText(o);
          Object.entries(patch).forEach(([k, v]) => {
            if (FONT_KEYS.includes(k)) tb?.set(k, v);
            else if (k === 'fill' || k === 'stroke' || k === 'strokeWidth') shape?.set(k, v);
            else o.set(k, v);
          });
          autoGrowNode(o); // bigger font → the shape grows and the label re-wraps
        } else {
          Object.entries(patch).forEach(([k, v]) => o.set(k, v));
        }
        o.setCoords();
      });
      c.requestRenderAll(); snapshot(); refreshSelection();
    },

    zoomIn: () => { const c = fcRef.current; const center = new fabric.Point(c.getWidth() / 2, c.getHeight() / 2); c.zoomToPoint(center, clamp(c.getZoom() * 1.2)); refreshZoom(); },
    zoomOut: () => { const c = fcRef.current; const center = new fabric.Point(c.getWidth() / 2, c.getHeight() / 2); c.zoomToPoint(center, clamp(c.getZoom() / 1.2)); refreshZoom(); },
    setZoomLevel: (z) => { const c = fcRef.current; const center = new fabric.Point(c.getWidth() / 2, c.getHeight() / 2); c.zoomToPoint(center, clamp(z)); refreshZoom(); },
    resetZoom: () => { const c = fcRef.current; c.setViewportTransform([1, 0, 0, 1, 0, 0]); refreshZoom(); },
    fitToScreen: () => {
      const c = fcRef.current; const objs = c.getObjects();
      if (!objs.length) { c.setViewportTransform([1, 0, 0, 1, 0, 0]); refreshZoom(); return; }
      const rect = objs.reduce((acc, o) => {
        const b = o.getBoundingRect();
        return { l: Math.min(acc.l, b.left), t: Math.min(acc.t, b.top), r: Math.max(acc.r, b.left + b.width), b: Math.max(acc.b, b.top + b.height) };
      }, { l: Infinity, t: Infinity, r: -Infinity, b: -Infinity });
      const pad = 80;
      const cw = c.getWidth(), ch = c.getHeight();
      // Fit to make everything visible, but never magnify past 100% (avoids a
      // tiny single object zooming the canvas to 500%).
      const z = clamp(Math.min(1, (cw - pad) / (rect.r - rect.l), (ch - pad) / (rect.b - rect.t)));
      c.setViewportTransform([z, 0, 0, z, 0, 0]);
      const cx = (rect.l + rect.r) / 2, cy = (rect.t + rect.b) / 2;
      const vpt = c.viewportTransform;
      vpt[4] = cw / 2 - cx * z; vpt[5] = ch / 2 - cy * z;
      c.setViewportTransform(vpt); refreshZoom();
    },

    addImageFromFile: async (file) => {
      if (!file) return;
      if (file.size > MAX_IMAGE_SIZE) { toast.error('Image exceeds 10 MB'); return; }
      const reader = new FileReader();
      const dataURL = await new Promise((res) => { reader.onload = () => res(reader.result); reader.readAsDataURL(file); });
      const img = await fabric.FabricImage.fromURL(dataURL, { crossOrigin: 'anonymous' });
      const c = fcRef.current;
      const max = Math.min(c.getWidth(), c.getHeight()) * 0.6 / c.getZoom();
      if (img.width > max) img.scaleToWidth(max);
      const center = c.getScenePoint ? c.getVpCenter?.() : null;
      img.set({ left: (center?.x ?? 100), top: (center?.y ?? 100), originX: 'center', originY: 'center' });
      withMeta(img, 'image');
      c.add(img); c.setActiveObject(img); c.requestRenderAll(); snapshot(); refreshSelection();
      store.getState().setActiveTool('select');
    },

    exportImage: async ({ format, quality = 0.92, multiplier = 2, includeBackground = true, transparent = false }) => {
      const c = fcRef.current;
      if (format === 'json') return JSON.stringify(c.toObject(EXTRA_PROPS), null, 2);
      if (format === 'svg') return c.toSVG();
      const bgConf = BACKGROUNDS[live.current.background] || BACKGROUNDS.light;
      const prev = c.backgroundColor;
      if (includeBackground && !transparent) c.backgroundColor = bgConf.color || '#ffffff';
      if (format === 'jpeg' && !c.backgroundColor) c.backgroundColor = '#ffffff';
      c.requestRenderAll();
      const dataURL = c.toDataURL({ format: format === 'jpeg' ? 'jpeg' : 'png', quality, multiplier });
      c.backgroundColor = prev; c.requestRenderAll();
      return dataURL;
    },

    // PNG thumbnail (small) for the listing card.
    thumbnail: () => {
      const c = fcRef.current;
      try { return c.toDataURL({ format: 'png', multiplier: 0.25, quality: 0.6 }); }
      catch { return ''; }
    },

    clearCanvas: () => {
      const c = fcRef.current; c.getObjects().slice().forEach((o) => c.remove(o));
      c.discardActiveObject(); c.requestRenderAll(); snapshot(); refreshSelection();
    },

    insertTemplate: (name) => {
      const c = fcRef.current;
      const ctr = vpCenter();
      const objs = templateObjects(name, ctr.x, ctr.y);
      if (!objs.length) return;
      if (c.getObjects().length + objs.length > MAX_OBJECTS) { toast.error(`Max ${MAX_OBJECTS} objects`); return; }
      objs.forEach((o) => c.add(o));
      c.discardActiveObject();
      const sel = new fabric.ActiveSelection(objs, { canvas: c });
      c.setActiveObject(sel);
      c.requestRenderAll(); snapshot(); refreshSelection();
      store.getState().setActiveTool('select');
    },

    // ── Frames + presentation ────────────────────────────────────────────────
    // A frame is a Group(rect + title label) with customType 'frame' — a named
    // region you can step through in presentation mode. It serialises natively
    // (children round-trip) so no custom prop registration is needed.
    insertFrame: () => {
      const c = fcRef.current;
      if (c.getObjects().length >= MAX_OBJECTS) { toast.error(`Max ${MAX_OBJECTS} objects`); return; }
      const ctr = vpCenter();
      const w = 900, h = 560;
      const n = c.getObjects().filter((o) => o.customType === 'frame').length + 1;
      const rect = new fabric.Rect({
        width: w, height: h, originX: 'center', originY: 'center', left: ctr.x, top: ctr.y,
        fill: 'rgba(148,163,184,0.06)', stroke: '#94a3b8', strokeWidth: 1.5, strokeDashArray: [8, 6], rx: 10, ry: 10,
      });
      const title = new fabric.Textbox(`Frame ${n}`, {
        width: w - 32, fontSize: 15, fontFamily: TEXT_DEFAULTS.fontFamily, fill: '#475569',
        fontWeight: '700', textAlign: 'left', originX: 'center', originY: 'center',
        left: ctr.x, top: ctr.y - h / 2 + 22,
      });
      const g = new fabric.Group([rect, title], { subTargetCheck: true });
      withMeta(g, 'frame');
      c.add(g); c.sendObjectToBack(g);
      c.setActiveObject(g); c.requestRenderAll(); snapshot(); refreshSelection();
      store.getState().setActiveTool('select');
    },

    // Frames sorted top-to-bottom then left-to-right (reading order).
    getFrames: () => {
      const c = fcRef.current;
      return c.getObjects()
        .filter((o) => o.customType === 'frame')
        .map((o) => {
          const b = o.getBoundingRect();
          const tb = o.getObjects?.().find((x) => x.type === 'textbox');
          return { id: o.id, name: tb?.text || 'Frame', rect: { left: b.left, top: b.top, width: b.width, height: b.height } };
        })
        .sort((a, z) => (Math.abs(a.rect.top - z.rect.top) > 40 ? a.rect.top - z.rect.top : a.rect.left - z.rect.left));
    },

    // Fit a scene-space rect into the viewport (used by presentation stepping).
    zoomToRect: ({ left, top, width, height }, pad = 60) => {
      const c = fcRef.current;
      const cw = c.getWidth(), ch = c.getHeight();
      const z = clamp(Math.min((cw - pad * 2) / width, (ch - pad * 2) / height));
      const cx = left + width / 2, cy = top + height / 2;
      c.setViewportTransform([z, 0, 0, z, cw / 2 - cx * z, ch / 2 - cy * z]);
      refreshZoom();
    },

    // Minimap: content bounds, per-object boxes and the viewport rect (scene coords).
    getMiniMapState: () => {
      const c = fcRef.current; const objs = c.getObjects();
      let l = Infinity, t = Infinity, r = -Infinity, b = -Infinity;
      const items = objs.map((o) => {
        const bb = o.getBoundingRect();
        l = Math.min(l, bb.left); t = Math.min(t, bb.top);
        r = Math.max(r, bb.left + bb.width); b = Math.max(b, bb.top + bb.height);
        return { l: bb.left, t: bb.top, w: bb.width, h: bb.height, fill: o.backgroundColor || (typeof o.fill === 'string' && o.fill ? o.fill : (typeof o.stroke === 'string' ? o.stroke : '#94a3b8')) };
      });
      const vpt = c.viewportTransform; const z = vpt[0] || 1;
      const view = { l: -vpt[4] / z, t: -vpt[5] / z, w: c.getWidth() / z, h: c.getHeight() / z };
      // Union content bounds with the viewport so the minimap always frames "you are here".
      if (!objs.length) { l = view.l; t = view.t; r = view.l + view.w; b = view.t + view.h; }
      else { l = Math.min(l, view.l); t = Math.min(t, view.t); r = Math.max(r, view.l + view.w); b = Math.max(b, view.t + view.h); }
      return { bounds: { l, t, r, b }, items, view };
    },
    panToScenePoint: (sx, sy) => {
      const c = fcRef.current; const z = c.getZoom(); const vpt = c.viewportTransform;
      vpt[4] = c.getWidth() / 2 - sx * z; vpt[5] = c.getHeight() / 2 - sy * z;
      c.setViewportTransform(vpt); refreshZoom();
    },
  }), [snapshot, applyHistory, refreshSelection, refreshCounts, refreshZoom, store, withMeta, applyToolConfig, vpCenter, templateObjects, applyLockState, autoGrowNode]);

  return (
    <div ref={wrapRef} className="cv-stage">
      <canvas ref={canvasElRef} />
    </div>
  );
});

export default CanvasCore;
